import { DateTime, WeekdayNumbers } from "luxon";
import { Calendar, CalendarProps, CalendarValue } from "../../Calendar";
import { IanaZone } from "../../../types";
import { TimezoneMessage } from "./TimezoneMessage";
import { isoToUtcDate } from "./utils";
import { useMemo } from "react";

type DateFieldSingleCalendarProps = Omit<
  CalendarProps,
  | "onSelection"
  | "value"
  | "onKeyDown"
  | "defaultFocusedDate"
  | "range"
  | "defaultTimeZone"
  | "unavailable"
  | "minDate"
  | "maxDate"
  | "defaultValue"
  | "_disableAutofocus"
> & {
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  value: DateTime | null;
  onSelection: (date: DateTime) => void;
  minDate?: DateTime | null;
  maxDate?: DateTime | null;
  unavailable?: { dates?: DateTime[]; daysOfWeek?: WeekdayNumbers[] };
  timezone?: IanaZone;
};

export function DateFieldSingleCalendar({
  onKeyDown,
  value,
  onSelection,
  minDate,
  maxDate,
  unavailable,
  timezone: timezoneProp,
  ...rest
}: DateFieldSingleCalendarProps) {
  const timezone: IanaZone = useMemo(() => {
    return (
      timezoneProp ??
      (Intl.DateTimeFormat().resolvedOptions().timeZone as IanaZone)
    );
  }, [timezoneProp]);

  const handleCalendarSelection = (data: { value: CalendarValue }) => {
    if (data.value) {
      const date = isoToUtcDate(data.value);
      onSelection(date);
    }
  };

  return (
    <>
      <Calendar
        {...rest}
        range={false}
        onKeyDown={onKeyDown}
        defaultFocusedDate={
          value?.toISODate() || DateTime.now().setZone(timezone).toISODate()
        }
        value={value?.toISODate() || undefined}
        onSelection={handleCalendarSelection}
        defaultTimeZone={timezone}
        minDate={minDate?.toISODate() ?? undefined}
        maxDate={maxDate?.toISODate() ?? undefined}
        unavailable={
          unavailable
            ? {
                dates: unavailable.dates?.map((d) => d.toISODate() ?? ""),
                daysOfWeek: unavailable.daysOfWeek,
              }
            : undefined
        }
        _disableAutofocus
        _disableFocus
      />
      <TimezoneMessage timezone={timezone} />
    </>
  );
}
